// Page-level date range filter: a preset pair plus a custom from/to range.
// The selected range scopes every component of a usage tab: chart, tiles, tables.

export type RangePreset = '7d' | '30d' | 'custom';

export interface DateRange {
  from: string; // YYYY-MM-DD inclusive
  to: string; // YYYY-MM-DD inclusive
}

// Control state. from/to only matter for the custom preset.
export interface RangeFilter {
  preset: RangePreset;
  from: string;
  to: string;
}

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Strict YYYY-MM-DD to a local-midnight Date. Null for anything else,
// including partial input like 2026-9 and impossible dates like 2026-02-30.
export function fromISODate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toISODate(d) === s ? d : null;
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function presetRange(preset: '7d' | '30d', today: Date): DateRange {
  return { from: toISODate(addDays(today, preset === '7d' ? -6 : -29)), to: toISODate(today) };
}

// Starting point for the custom preset: the last 14 days incl. today.
export function customDefaultRange(today: Date): DateRange {
  return { from: toISODate(addDays(today, -13)), to: toISODate(today) };
}

// Today's UTC calendar date, carried as a local-midnight Date so the helpers
// above keep using plain local getters. The usage pages are UTC, so a browser
// west of UTC must not end the range a day early.
export function todayUTC(): Date {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

// Null while a custom range is incomplete or inverted: callers then skip the
// fetch and show a hint instead of querying a meaningless range.
export function resolveRange(filter: RangeFilter, today: Date): DateRange | null {
  if (filter.preset !== 'custom') return presetRange(filter.preset, today);
  if (!filter.from || !filter.to || filter.from > filter.to) return null;
  return { from: filter.from, to: filter.to };
}
